import type { HumanoidRobot } from "@/types";

/**
 * Parse units deployed string to a numeric value
 * Handles formats like "1,000+", "500", "1000 units", etc.
 */
export const parseUnitsDeployed = (value: string | null | undefined): number => {
  if (!value) return 0;
  const cleaned = value.replace(/[^0-9]/g, '');
  return parseInt(cleaned, 10) || 0;
};

/**
 * Calculate a basic completeness score for sorting/display purposes
 * Returns a number from 0-100 based on how complete the robot's data is
 */
export const getBasicCompletenessScore = (robot: HumanoidRobot): number => {
  let score = 0;
  if (robot.images && robot.images.length > 0 && robot.images[0] !== '') score += 50;
  if (robot.description && robot.description !== 'No description available') score += 10;
  if (robot.about && robot.about !== 'No information available') score += 10;
  if (robot.useCases && robot.useCases.length > 0) score += 5;
  if (robot.specs && Object.keys(robot.specs).length > 0) score += 5;
  if (robot.website) score += 3;
  if (robot.priceRange) score += 3;
  if (robot.aiCapabilities && robot.aiCapabilities.length > 0) score += 3;
  if (robot.videos && robot.videos.length > 0) score += 2;
  return score;
};

/**
 * Robot data structure for admin completeness scoring (uses snake_case DB fields)
 */
export interface RobotForCompletenessCheck {
  description?: string | null;
  about?: string | null;
  images?: string[] | null;
  videos?: string[] | null;
  specs?: Record<string, unknown> | null;
  use_cases?: string[] | null;
  autonomy_level?: string | null;
  price_range?: string | null;
  units_deployed?: string | null;
  website?: string | null;
}

/**
 * Calculate detailed completeness score with list of missing fields
 * Used in admin interfaces to show what data needs to be filled in
 */
export const getDetailedCompletenessScore = (robot: RobotForCompletenessCheck): { score: number; missing: string[] } => {
  const missing: string[] = [];
  const total = 10;
  let filled = 0;

  if (robot.description && robot.description !== 'No description provided') filled++; else missing.push('Description');
  if (robot.about && robot.about !== 'No about information provided') filled++; else missing.push('About');
  if (robot.images && robot.images.length > 0) filled++; else missing.push('Images');
  if (robot.videos && robot.videos.length > 0) filled++; else missing.push('Videos');
  if (robot.specs && Object.keys(robot.specs).length > 0) filled++; else missing.push('Specs');
  if (robot.use_cases && robot.use_cases.length > 0) filled++; else missing.push('Use Cases');
  if (robot.autonomy_level && robot.autonomy_level !== 'unknown') filled++; else missing.push('Autonomy Level');
  if (robot.price_range) filled++; else missing.push('Price Range');
  if (robot.units_deployed) filled++; else missing.push('Units Deployed');
  if (robot.website) filled++; else missing.push('Website');

  return { score: Math.round((filled / total) * 100), missing };
};

/**
 * Calculate a feature score for ranking robots (used in featured sections)
 * Higher score = more noteworthy robot
 */
export const getFeaturedScore = (robot: HumanoidRobot): number => {
  let score = 0;
  
  // Production status is most important
  if (robot.status === 'production') score += 50;
  else if (robot.status === 'prototype') score += 30;
  else if (robot.status === 'concept') score += 15;
  
  // Units deployed indicates market traction
  const units = parseUnitsDeployed(robot.unitsDeployed);
  if (units > 1000) score += 40;
  else if (units > 100) score += 25;
  else if (units > 0) score += 10;
  
  // Having images is crucial for display
  if (robot.images && robot.images.length > 0) score += 20;
  
  // Recent year indicates relevance
  const currentYear = new Date().getFullYear();
  if (robot.year >= currentYear - 1) score += 15;
  else if (robot.year >= currentYear - 3) score += 10;
  
  // Video content is engaging
  if (robot.videos && robot.videos.length > 0) score += 10;
  
  return score;
};
